'use client'

import { useActionState, useEffect, useState, useTransition } from 'react'

import { Button, Divider, PasswordInput, Stack, Text, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import { UPDATE_PASSWORD_INITIAL_STATE } from '@/lib/api/auth/auth.constants'
import { updatePassword } from '@/lib/api/auth/auth.service'
import type {
  UpdatePasswordFormData,
  UpdatePasswordFormState,
} from '@/lib/api/auth/auth.types'
import { readReturnUrl } from '@/lib/auth/return-url'
import { showSuccess } from '@/lib/notifications'
import { ZodPasswordCheck } from '@/utils/zod'

import FormErrorMessage from '../components/form-error-message'
import LinkBackLogin from '../components/link-back-login'

const UpdatePasswordForm = () => {
  const router = useRouter()
  const tAuth = useTranslations('auth')
  const tCommon = useTranslations('common')

  const [, startTransition] = useTransition()
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)

  const schema = z
    .object({
      password: ZodPasswordCheck(tAuth, 'update-password'),
      confirmPassword: ZodPasswordCheck(tAuth, 'update-password'),
    })
    .refine((data) => data.password === data.confirmPassword, {
      error: tAuth('update-password.form.fields.password-confirmation.error.match'),
      path: ['confirmPassword'],
    })

  const form = useForm<UpdatePasswordFormData>({
    mode: 'uncontrolled',
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: zod4Resolver(schema),
  })

  const [state, submitAction, isPending] = useActionState<
    UpdatePasswordFormState,
    UpdatePasswordFormData
  >(updatePassword, UPDATE_PASSWORD_INITIAL_STATE)

  const handleSubmit = (values: UpdatePasswordFormData) => {
    startTransition(() => {
      submitAction(values)
    })
  }

  useEffect(() => {
    if (state.success) {
      showSuccess({
        title: tAuth('update-password.notifications.password-updated.title'),
        message: tAuth('update-password.notifications.password-updated.description'),
      })
      router.push(readReturnUrl())
    }
  }, [state.success, tAuth, router])

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap='2xl'>
        <Stack gap='xl'>
          <Stack gap='xs' align='center'>
            <Title size='h3' ta='center'>
              {tAuth('update-password.title')}
            </Title>
            <Text fz='xs' ta='center' w='70%'>
              {tAuth('update-password.description')}
            </Text>
          </Stack>
          <Stack gap='lg'>
            <Stack gap='md'>
              <PasswordInput
                description={tAuth('update-password.form.fields.password.description')}
                label={tAuth('update-password.form.fields.password.label')}
                onVisibilityChange={setIsPasswordVisible}
                placeholder={tAuth('update-password.form.fields.password.placeholder')}
                opacity={isPending ? 0.2 : 1}
                required
                size='xs'
                style={{
                  pointerEvents: isPending ? 'none' : 'auto',
                  transition: 'opacity 0.5s ease-out',
                }}
                visible={isPasswordVisible}
                {...form.getInputProps('password')}
              />

              <PasswordInput
                label={tAuth('update-password.form.fields.password-confirmation.label')}
                onVisibilityChange={setIsPasswordVisible}
                opacity={isPending ? 0.2 : 1}
                placeholder={tAuth(
                  'update-password.form.fields.password-confirmation.placeholder',
                )}
                required
                size='xs'
                style={{
                  pointerEvents: isPending ? 'none' : 'auto',
                  transition: 'opacity 0.5s ease-out',
                }}
                visible={isPasswordVisible}
                {...form.getInputProps('confirmPassword')}
              />
            </Stack>

            <Button
              autoContrast
              loading={isPending}
              loaderProps={{ type: 'dots' }}
              type='submit'
              size='xs'
            >
              {tAuth('update-password.form.submit.label')}
            </Button>
          </Stack>
          <FormErrorMessage message={state.errorMessage} />
        </Stack>
        <Divider
          color='var(--mantine-color-border-surface)'
          label={tCommon('or')}
          labelPosition='center'
        />
        <LinkBackLogin>{tAuth('update-password.back-button')}</LinkBackLogin>
      </Stack>
    </form>
  )
}

export default UpdatePasswordForm
